import React, { useMemo, useEffect } from 'react';
import throttle from 'lodash/throttle';
import {
  default as OnScrollBottom,
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import Pagination from '@wix/da-shared-react/pkg/Pagination';
import generatePaginationUrl from '../../../../../../utils/generatePaginationUrl';
import s from './FeedPagination.scss';

const DISTANCE_FROM_BOTTOM_TO_FETCH_MORE = 1000;
const FETCH_MORE_THROTTLE = 300;

export interface Props {
  streamId: string;
  hasMore: boolean;
  fetchMore: () => void;
  isFetching: boolean;
  currentPage: number;
  totalPages: number;
  requestUrl: string;
  isInfiniteScroll: boolean;
  isDuperbrowseActive: boolean;
  error?: string;
  children?: React.ReactNode;
}

const FeedPagination: React.FC<Props> = ({
  children,
  isFetching,
  hasMore,
  isDuperbrowseActive,
  isInfiniteScroll,
  currentPage,
  totalPages,
  requestUrl,
  error,
  fetchMore,
}) => {
  const handleScrollToBottom = useMemo(
    () =>
      throttle(() => {
        if (isFetching || isDuperbrowseActive || !hasMore || error) {
          return;
        }

        fetchMore();
      }, FETCH_MORE_THROTTLE),
    [fetchMore, hasMore, isFetching, isDuperbrowseActive, error]
  );

  useEffect(() => {
    return () => {
      handleScrollToBottom.cancel();
    };
  }, [handleScrollToBottom]);

  return (
    <>
      {children}
      {isFetching && <LoadingIndicator />}
      {!isInfiniteScroll && (
        <div className={s['paged']}>
          <Pagination
            currentPage={currentPage}
            totalPages={
              totalPages ? totalPages : hasMore ? currentPage + 1 : currentPage
            }
            showPageNumbers={!!totalPages}
            getPageUrl={page => generatePaginationUrl(page, requestUrl)}
          />
        </div>
      )}
      {isInfiniteScroll && (
        <OnScrollBottom
          checkOnMount
          checkOnUpdate={hasMore}
          onScrolledToBottom={handleScrollToBottom}
          distanceToBottom={DISTANCE_FROM_BOTTOM_TO_FETCH_MORE}
          distanceUnit={DISTANCE_UNIT.PX}
        />
      )}
    </>
  );
};
FeedPagination.displayName = 'FeedPagination';

export default FeedPagination;
